/* Shoppable video carousel — aidabicaj.com
 * Used by sections/shoppable-video-carousel.liquid
 *
 * - One instance per section, keyed by its root element. Safe to load this
 *   file any number of times (theme editor re-renders re-run it).
 * - Videos load and play only while on screen; they pause off screen.
 * - Theme editor: section load/unload and block select are handled.
 */
(function () {
  'use strict';

  if (window.ShoppableVideoCarousel) {
    window.ShoppableVideoCarousel.scan(document);
    return;
  }

  var YT_ORIGIN = 'https://www.youtube-nocookie.com';
  var instances = new Map();
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function inPiP(video) {
    if (!video) return false;
    if (document.pictureInPictureElement === video) return true;
    return video.webkitPresentationMode === 'picture-in-picture';
  }

  function Carousel(root) {
    this.root = root;
    this.track = root.querySelector('[data-svc-track]');
    this.prev = root.querySelector('[data-svc-prev]');
    this.next = root.querySelector('[data-svc-next]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-svc-slide]'));
    this.states = new Map();
    this.controller = new AbortController();
    this.frame = 0;
    if (this.track) this.init();
  }

  Carousel.prototype.init = function () {
    var self = this;
    var signal = this.controller.signal;

    this.track.addEventListener('scroll', function () { self.scheduleArrows(); }, { passive: true, signal: signal });
    if (this.prev) this.prev.addEventListener('click', function () { self.page(-1); }, { signal: signal });
    if (this.next) this.next.addEventListener('click', function () { self.page(1); }, { signal: signal });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(function () { self.scheduleArrows(); });
      this.resizeObserver.observe(this.track);
    }

    this.slides.forEach(function (slide) { self.setupSlide(slide); });

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var state = self.states.get(entry.target);
          if (!state) return;
          state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.6;
          self.apply(state);
        });
      }, { threshold: [0, 0.6] });
      this.slides.forEach(function (slide) { self.observer.observe(slide); });
    } else {
      this.states.forEach(function (state) { state.visible = true; self.apply(state); });
    }

    this.updateArrows();
  };

  /* ---------- Arrows ---------- */

  Carousel.prototype.scheduleArrows = function () {
    var self = this;
    if (this.frame) return;
    this.frame = window.requestAnimationFrame(function () {
      self.frame = 0;
      self.updateArrows();
    });
  };

  Carousel.prototype.updateArrows = function () {
    if (!this.prev && !this.next) return;
    var max = this.track.scrollWidth - this.track.clientWidth;
    var overflow = max > 2;
    var left = this.track.scrollLeft;
    if (this.prev) {
      this.prev.hidden = !overflow;
      this.prev.disabled = left <= 2;
    }
    if (this.next) {
      this.next.hidden = !overflow;
      this.next.disabled = left >= max - 2;
    }
  };

  Carousel.prototype.step = function () {
    if (this.slides.length < 2) return this.track.clientWidth;
    return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
  };

  Carousel.prototype.page = function (direction) {
    var step = this.step();
    var perPage = Math.max(1, Math.floor((this.track.clientWidth + 1) / step));
    this.track.scrollBy({ left: direction * perPage * step, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  Carousel.prototype.goTo = function (blockId) {
    var slide = this.slides.filter(function (el) { return el.getAttribute('data-block-id') === blockId; })[0];
    if (!slide || !this.slides[0]) return;
    this.track.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  /* ---------- Slides & video ---------- */

  Carousel.prototype.setupSlide = function (slide) {
    var self = this;
    var signal = this.controller.signal;
    var state = {
      slide: slide,
      video: slide.querySelector('video'),
      iframe: slide.querySelector('iframe[data-svc-src]'),
      toggle: slide.querySelector('[data-svc-toggle]'),
      pip: slide.querySelector('[data-svc-pip]'),
      visible: false,
      userPaused: reduceMotion,
      ytLoaded: false,
      ytReady: false
    };
    this.states.set(slide, state);

    if (state.video) {
      state.video.muted = true;
      state.video.defaultMuted = true;
      state.video.loop = true;
      state.video.playsInline = true;
      state.video.setAttribute('playsinline', '');
      state.video.addEventListener('leavepictureinpicture', function () { self.apply(state); }, { signal: signal });
      state.video.addEventListener('webkitpresentationmodechanged', function () { self.apply(state); }, { signal: signal });
    }

    if (state.toggle) {
      state.toggle.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        state.userPaused = !state.userPaused;
        self.apply(state);
      }, { signal: signal });
    }

    if (state.pip) this.setupPiP(state);

    this.setupProductPicker(slide);
    this.renderToggle(state);
  };

  /* ---------- Several products per card ---------- */

  Carousel.prototype.setupProductPicker = function (slide) {
    var picks = Array.prototype.slice.call(slide.querySelectorAll('[data-svc-pick]'));
    var panels = Array.prototype.slice.call(slide.querySelectorAll('[data-svc-panel]'));
    if (picks.length < 2) return;

    picks.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation(); // don't trigger a full-card link
        var index = button.getAttribute('data-svc-pick');
        picks.forEach(function (other) {
          var active = other === button;
          other.classList.toggle('is-active', active);
          other.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        panels.forEach(function (panel) {
          panel.classList.toggle('is-active', panel.getAttribute('data-svc-panel') === index);
        });
      }, { signal: this.controller.signal });
    }, this);
  };

  Carousel.prototype.renderToggle = function (state) {
    state.slide.classList.toggle('is-paused', state.userPaused);
    if (state.toggle) state.toggle.setAttribute('aria-label', state.userPaused ? 'Play video' : 'Pause video');
  };

  Carousel.prototype.apply = function (state) {
    var shouldPlay = state.visible && !state.userPaused;
    this.renderToggle(state);

    if (state.video) {
      if (shouldPlay) {
        var attempt = state.video.play();
        if (attempt && attempt.catch) attempt.catch(function () {});
      } else if (!inPiP(state.video)) {
        state.video.pause();
      }
    }

    if (state.iframe) {
      if (shouldPlay && !state.ytLoaded) {
        this.loadYouTube(state); // embed URL autoplays
      } else {
        this.youtube(state, shouldPlay ? 'playVideo' : 'pauseVideo');
      }
    }
  };

  Carousel.prototype.loadYouTube = function (state) {
    var self = this;
    var iframe = state.iframe;
    state.ytLoaded = true;
    iframe.addEventListener('load', function () {
      iframe.classList.add('is-loaded');
      state.ytReady = true;
      // State may have changed while the player loaded
      window.setTimeout(function () {
        self.youtube(state, state.visible && !state.userPaused ? 'playVideo' : 'pauseVideo');
      }, 400);
    }, { once: true, signal: this.controller.signal });
    iframe.src = iframe.getAttribute('data-svc-src');
  };

  Carousel.prototype.youtube = function (state, command) {
    if (!state.ytReady || !state.iframe.contentWindow) return;
    state.iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: command, args: [] }), YT_ORIGIN);
  };

  /* ---------- Picture-in-picture (uploaded videos) ---------- */

  Carousel.prototype.setupPiP = function (state) {
    var self = this;
    var video = state.video;
    var button = state.pip;
    var webkit = !!(video && typeof video.webkitSetPresentationMode === 'function');
    var standard = !!document.pictureInPictureEnabled;

    if (!video || video.disablePictureInPicture || !(standard || webkit)) {
      button.hidden = true;
      return;
    }
    button.hidden = false;

    function enter() {
      state.userPaused = false;
      self.apply(state);
      if (standard) {
        video.requestPictureInPicture().catch(function (error) {
          if (window.console) console.warn('[shoppable-video] PiP failed:', error);
        });
      } else {
        video.webkitSetPresentationMode('picture-in-picture');
      }
    }

    button.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();

      if (document.pictureInPictureElement === video) {
        document.exitPictureInPicture();
        return;
      }
      if (webkit && video.webkitPresentationMode === 'picture-in-picture') {
        video.webkitSetPresentationMode('inline');
        return;
      }

      if (video.readyState >= 1) {
        enter();
      } else {
        video.preload = 'metadata';
        video.addEventListener('loadedmetadata', enter, { once: true, signal: self.controller.signal });
        video.load();
      }
    }, { signal: this.controller.signal });
  };

  /* ---------- Teardown ---------- */

  Carousel.prototype.destroy = function () {
    this.controller.abort();
    if (this.observer) this.observer.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.frame) window.cancelAnimationFrame(this.frame);
    this.states.forEach(function (state) {
      if (state.video && !inPiP(state.video)) state.video.pause();
    });
    this.states.clear();
  };

  /* ---------- Registry ---------- */

  function create(root) {
    if (instances.has(root)) return;
    try {
      instances.set(root, new Carousel(root));
    } catch (error) {
      if (window.console) console.error('[shoppable-video] init failed:', error);
    }
  }

  function remove(root) {
    var instance = instances.get(root);
    if (!instance) return;
    instance.destroy();
    instances.delete(root);
  }

  function scan(scope) {
    instances.forEach(function (instance, root) {
      if (!root.isConnected) remove(root);
    });
    var container = scope || document;
    if (container.matches && container.matches('[data-svc]')) create(container);
    Array.prototype.forEach.call(container.querySelectorAll('[data-svc]'), create);
  }

  function rootsIn(target) {
    if (!target || !target.querySelectorAll) return [];
    return Array.prototype.slice.call(target.querySelectorAll('[data-svc]'));
  }

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    rootsIn(event.target).forEach(remove);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target && event.target.closest && event.target.closest('[data-svc]');
    var instance = root && instances.get(root);
    if (instance) instance.goTo(event.detail.blockId);
  });

  window.ShoppableVideoCarousel = { scan: scan, instances: instances };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document);
  }
})();
